/**
  * @class mmodOfficeWfDopplerCalibrations
  * @description
  *
  * # Purpose
  * Module displays Doppler radar calibrations imported from vehicles.
  */

import mmodWorkflow from 'mmodWorkflow'
const Doppler = require('../../msvc/msvcDoppler/doppler.js')

export default class mmodOfficeWfDopplerCalibrations extends mmodWorkflow {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: { type: 'full' },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
      workflow_server: {
        type: 'string',
        default: ':msvrWfDopplerCalibrations',
        description: 'Listener acting as the server-side for the workflow',
      },
      tabular: {
        type: 'boolean',
        default: true,
      },
      delay_initial_search: { type: 'integer', description: 'delay of initial search', default: 20 },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sent by &lt;mele-container> or derived class to parent module when form is submitted
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.holder - key attribute of the form
  * @param {string} [params.button] - key attribute of the button
  * @param {string} [params.datakey] - key attribute of the content
  * @param {object} params.values - hash of key:value of contained editable inputs
  * @param {boolean} [params.invalid] - true if any of form's inputs is invalid
  * @param {boolean} [params.conflict] - true if any of form's inputs has conflict
  */
  async mmFormSubmit(sender, params) {
    const { button } = params
    if(button==='window_print') {
      window.print()
    }else if(button==='import_file') {
      this.publish('mmodOfficeImport', 'mmImportPrompt', {
        folders: false,
        selected: ['mmDopplerHit'],
      })
    }else if(button==='import_folder') {
      this.publish('mmodOfficeImport', 'mmImportPrompt', {
        folders: true,
        selected: ['mmDopplerHit'],
      })
    }else if(button==='ACTION:EXPORT') {
      this.exportLocal(params.datakey)
    }else{
      super.mmFormSubmit(sender, params)
    }
  } // mmFormSubmit

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * opens file selection dialog for user to select files for import
  */
  async exportLocal(id) {
    try{
      const reply = await this.request(':msvrWfDopplerCalibrations', 'mmAction', {
        action: 'EXPORT',
        id,
      })
      const data = reply?.params?.list?.[0]
      const binary = data?.export
      if(binary && binary.length>0) {
        const blob = new Blob([binary], { type: 'application/octet-stream' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = this.exportFileName(data)+'.mm'
        a.click()
        URL.revokeObjectURL(url)
      }else {
        this.modalError('wf.dopplercalibrations.export.NoData', 'wf.dopplercalibrations.export.Error')
      }
    }catch(e) {
      this.modalError(String(e), 'wf.dopplercalibrations.export.Error')
    }
  } // importLocal

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * called on form_ready hook of detail form
  */
  async formready(form_name, record, form) {
    if(form_name === 'read') {
      // enhance input image
      form.record_data = record
      const input_img = form.find('img')
      form.find('img.burned').src = ''
      const enhance = this.enhanceReadFormImage.bind(this)
      if(input_img.complete && input_img.naturalWidth > 0) {
        this.enhanceReadFormImage()
      }else {
        input_img.onload = enhance
        input_img.onerror = enhance
      }
    }
  } // formready

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * enhances "read" form image by defined artefacts
  */
  async enhanceReadFormImage() {
    const form = this.el.find('mele-view[key=read][role*=bottom]')
    const data = form?.record_data
    if(!data) return
    const input_img = form.find('img')
    const output_img = form.find('img.burned')
    if(input_img.complete && input_img.naturalWidth > 0) {
      this.enhanceImage(data, input_img, output_img)
    }
  } // formready

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to set language
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.lang] - desired language in 2 character ISO 639-1
  */
  mmSetLanguage(sender, params) {
    // if language changed, update image
    super.mmSetLanguage(sender, params)
    this.enhanceReadFormImage()
  } // mmSetLanguage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * called on form_ready hook of detail form
  */
  reconstructPortion(data, domain) {
    const out = {}
    try{
      const props = this._schema_types[domain].properties
      for(const key in props) {
        if(key in data) {
          if(props[key].type === 'integer') {
            out[key] = parseInt(data[key])
          }else if(props[key].type === 'number') {
            out[key] = parseFloat(data[key])
          }else {
            out[key] = data[key]
          }
        }
      }
    }catch(e) {
      // ignore
    }
    return out
  } // reconstructPortion

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * draws selected artefacts on the image
  * possible artefacts: lanes, markers
  * @param {object} data - data to be displayed
  * @param {object} input_img - image element to be used as input
  * @param {object} output_img - image element to be used as output (could be same as input element)
  */
  async enhanceImage(data, input_img, output_img) {
    for(const key in data) {
      const numeric = Doppler.calibrationProperty(key, data[key])?.numeric
      if(!isNaN(numeric)) {
        data[key] = numeric
      }
    }
    const data_uri = await Doppler.getCalibrationImage(input_img, data, data, this.el.language)
    output_img.src = data_uri
  } // enhanceImage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * show enlarged canvas with doppler image
  */
  showEnlargedImage(source_img) {
    this.modalOpen({
      key: 'mmodOfficeWfDopplerCalibrationsPreview',
      position: 'center',
      outclick: ':modal.cancel',
      inclick: ':modal.cancel',
      class: 'modal-preview',
      items: [
        { tag: 'img', src: source_img.src },
      ],
    })
  } // showEnlargedImage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Sends by &lt;mele-button> to parent module when a button is clicked
   * @param {object} sender - message sender
   * @param {object} params - message parameters
   * @param {string} [params.holder] - key attribute of button's parent container
   * @param {string} params.button - key attribute of the button
   * @param {integer} [params.count] - number of times button was clicked
   * @param {boolean} [params.long] - shows if press was long or not
   */
  mmButtonClicked(sender, params) {
    const { button } = params
    if(button === 'snapshot_download') {
      const form = this.el.find('mele-view[key=read][role*=bottom]')
      if(form && form.record_data) {
        const img = form.find('img.burned')
        if(img && img.src) {
          const el = document.createElement('a')
          el.style.display = 'none'
          el.setAttribute('href', img.src)
          el.setAttribute('download', this.exportFileName(form.record_data)+'.jpg')
          document.body.appendChild(el)
          el.click()
          document.body.removeChild(el)
        }
      }
    }else{
      super.mmButtonClicked(sender, params)
    }
  } // mmButtonClicked

  exportFileName(data) {
    const type = 'calibration'
    const sn = data?.sn || 'unknownsn'
    const channel = data?.channel || 'unknownchannel'
    const event_dt = data?.calibration_dt || 'unknowndt'
    const d = new Date(event_dt/1000)
    const dt_str =
      String(d.getFullYear()).padStart(4, 0)+'-' +
      String(d.getMonth()+1).padStart(2, 0)+'-' +
      String(d.getDate()).padStart(2, 0)+'_' +
      String(d.getHours()).padStart(2, 0)+'-' +
      String(d.getMinutes()).padStart(2, 0)+'-' +
      String(d.getSeconds()).padStart(2, 0)
    const filename = `mosy_doppler_${type}_${sn}_${channel}_${event_dt}_${dt_str}`
    return filename
  } // exportFileName
} // mmodOfficeWfDopplerCalibrations
